import React, { useState, useEffect } from 'react';
import type { TaxFormQuestionnaireProps, FormType, FormStatus } from '../types';
import { FormSelector } from './FormSelector';
import { W9Questionnaire } from './W9Questionnaire';
import { W8BENQuestionnaire } from './W8BENQuestionnaire';

export const TaxFormQuestionnaire: React.FC<TaxFormQuestionnaireProps> = ({
  bearerToken,
  formType = 'auto',
  language = 'en-us',
  prefillData,
  onSubmit,
  onSuccess,
  onError,
  onProgress,
  onFormTypeSelected,
  onLoad,
  environment = 'production',
  customStyles,
  theme,
  debug = false,
}) => {
  const [selectedFormType, setSelectedFormType] = useState<FormType | null>(
    formType === 'auto' ? null : formType
  );
  const [status, setStatus] = useState<FormStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  // Debug logging
  useEffect(() => {
    if (debug) {
      console.log('[W9SDK] Initialized', {
        formType,
        language,
        environment,
        hasBearerToken: !!bearerToken,
        hasPrefillData: !!prefillData,
      });
    }
  }, [debug, formType, language, environment, bearerToken, prefillData]);

  // Validate bearer token
  useEffect(() => {
    if (!bearerToken) {
      const err = new Error('Bearer token is required');
      setError(err);
      setStatus('error');
      if (onError) onError(err);
      if (debug) console.error('[W9SDK] Missing bearer token');
    }
  }, [bearerToken, onError, debug]);

  // Notify parent when loaded
  useEffect(() => {
    if (onLoad && status === 'idle' && bearerToken) {
      onLoad();
      if (debug) console.log('[W9SDK] Component loaded');
    }
  }, [onLoad, status, bearerToken, debug]);

  // Handle form type selection
  const handleFormTypeSelection = (type: FormType) => {
    setSelectedFormType(type);
    if (onFormTypeSelected) {
      onFormTypeSelected(type);
    }
    if (debug) {
      console.log('[W9SDK] Form type selected:', type);
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    setStatus('submitting');
    if (onSubmit) {
      onSubmit();
    }
    if (debug) {
      console.log('[W9SDK] Form submission started');
    }
  };

  // Handle successful submission
  const handleSuccess = (result: any) => {
    setStatus('completed');
    if (onSuccess) {
      onSuccess(result);
    }
    if (debug) {
      console.log('[W9SDK] Form submitted successfully:', result);
    }
  };

  // Handle submission error
  const handleError = (err: Error) => {
    setStatus('error');
    setError(err);
    if (onError) {
      onError(err);
    }
    if (debug) {
      console.error('[W9SDK] Form submission error:', err);
    }
  };

  // Handle step progress
  const handleProgress = (step: number, total: number) => {
    if (onProgress) {
      onProgress(step, total);
    }
    if (debug) {
      console.log(`[W9SDK] Progress: Step ${step} of ${total}`);
    }
  };

  // Apply custom theme
  const themeStyles: React.CSSProperties = {
    ...(theme?.primaryColor && { '--w9-primary-color': theme.primaryColor }),
    ...(theme?.fontFamily && { '--w9-font-family': theme.fontFamily }),
    ...(theme?.borderRadius && { '--w9-border-radius': theme.borderRadius }),
    ...(theme?.backgroundColor && { '--w9-background-color': theme.backgroundColor }),
    ...(theme?.textColor && { '--w9-text-color': theme.textColor }),
    ...customStyles,
  } as React.CSSProperties;

  // Error state
  if (status === 'error' && error) {
    return (
      <div className="w9-error-container" style={themeStyles}>
        <div className="w9-error-icon">⚠️</div>
        <h3 className="w9-error-title">Unable to Load Tax Form</h3>
        <p className="w9-error-message">{error.message}</p>
        <p className="w9-error-help">
          Please contact support if this problem persists.
        </p>
      </div>
    );
  }

  // Loading state
  if (!bearerToken || status === 'loading') {
    return (
      <div className="w9-loading-container" style={themeStyles}>
        <div className="w9-spinner"></div>
        <p>Loading tax form...</p>
      </div>
    );
  }

  return (
    <div className="w9-questionnaire-container" style={themeStyles} data-environment={environment}>
      {/* Step 1: Form type selection (if auto mode) */}
      {formType === 'auto' && !selectedFormType && (
        <FormSelector
          onFormTypeSelected={handleFormTypeSelection}
        />
      )}

      {/* Step 2: Show selected form */}
      {(selectedFormType === 'w9' || formType === 'w9') && (
        <W9Questionnaire
          bearerToken={bearerToken}
          language={language}
          prefillData={prefillData as any}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onError={handleError}
          onProgress={handleProgress}
          environment={environment}
          debug={debug}
        />
      )}

      {(selectedFormType === 'w8ben' || formType === 'w8ben') && (
        <W8BENQuestionnaire
          bearerToken={bearerToken}
          language={language}
          prefillData={prefillData as any}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onError={handleError}
          onProgress={handleProgress}
          environment={environment}
          debug={debug}
        />
      )}

      {/* Debug panel */}
      {debug && (
        <div className="w9-debug-panel">
          <h4>Debug Info</h4>
          <pre>
            {JSON.stringify(
              {
                status,
                selectedFormType,
                formType,
                language,
                environment,
                hasBearerToken: !!bearerToken,
                hasPrefillData: !!prefillData,
                error: error?.message,
              },
              null,
              2
            )}
          </pre>
        </div>
      )}
    </div>
  );
};

export default TaxFormQuestionnaire;
